import { zodResolver } from '@hookform/resolvers/zod';
import BoltIcon from '@mui/icons-material/Bolt';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import {
  Alert,
  Box,
  Button,
  Divider,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import {
  Link as RouterLink,
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { z } from 'zod';

import { isTotpRequired } from '../../features/auth/auth-api';
import { useLogin } from '../../hooks/useAuth';
import { apiClient } from '../../lib/api-client';
import { useAuthStore } from '../../stores/auth-store';

const schema = z.object({
  email: z.string().email('Adresse e-mail invalide'),
  password: z.string().min(1, 'Mot de passe requis'),
});

type FormValues = z.infer<typeof schema>;

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const ssoError = searchParams.get('sso_error');
  const status = useAuthStore((s) => s.status);
  const login = useLogin();
  const { data: ssoProviders } = useQuery({
    queryKey: ['auth', 'sso-providers'],
    queryFn: async () => {
      const { data } = await apiClient.get<{ azure: boolean }>('/v1/auth/sso/providers');
      return data;
    },
    staleTime: 5 * 60_000,
  });
  const [requiresTotp, setRequiresTotp] = useState<{ email: string; password: string } | null>(
    null,
  );
  const [totpCode, setTotpCode] = useState('');

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  if (status === 'authenticated') {
    return <Navigate to="/app" replace />;
  }

  function redirectAfterLogin() {
    const from = (location.state as LocationState | null)?.from?.pathname ?? '/app';
    navigate(from, { replace: true });
  }

  const onSubmit = handleSubmit((values) => {
    login.mutate(values, {
      onSuccess: (data) => {
        if (isTotpRequired(data)) {
          setRequiresTotp({ email: values.email, password: values.password });
        } else {
          redirectAfterLogin();
        }
      },
    });
  });

  function onSubmitTotp(e: React.FormEvent) {
    e.preventDefault();
    if (!requiresTotp || !totpCode) return;
    login.mutate(
      { ...requiresTotp, totpCode: totpCode.trim() },
      {
        onSuccess: (data) => {
          if (!isTotpRequired(data)) redirectAfterLogin();
        },
      },
    );
  }

  const errorMessage = login.error
    ? ((login.error as { response?: { data?: { message?: string } } }).response?.data?.message ??
      'Identifiants invalides')
    : null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        background: 'linear-gradient(135deg, #f8f7fa 0%, #e3fbf4 100%)',
      }}
    >
      {/* Panneau gauche — branding (desktop) */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' },
          flexDirection: 'column',
          justifyContent: 'space-between',
          p: 6,
          color: 'common.white',
          position: 'relative',
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 45%, #2fe79e 100%)',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background:
              'radial-gradient(at 0% 0%, rgba(249, 115, 22, 0.25) 0px, transparent 50%), radial-gradient(at 100% 100%, rgba(59, 130, 246, 0.4) 0px, transparent 50%)',
          },
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ position: 'relative' }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              bgcolor: 'rgba(255,255,255,0.15)',
              backdropFilter: 'blur(8px)',
              border: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <BoltIcon />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
              Premium Energy
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.85 }}>
              Plateforme de support
            </Typography>
          </Box>
        </Stack>

        <Box sx={{ position: 'relative' }}>
          <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, letterSpacing: '-0.02em' }}>
            Vos tickets,
            <br />
            gérés simplement.
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.85, maxWidth: 420 }}>
            Suivi en temps réel, SLA contrôlés, collaboration fluide entre vos équipes et vos
            clients.
          </Typography>
        </Box>

        <Stack direction="row" spacing={4} sx={{ position: 'relative' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              99.9%
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Disponibilité
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              SLA
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Pilotés à la minute
            </Typography>
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              RGPD
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Conforme par défaut
            </Typography>
          </Box>
        </Stack>
      </Box>

      {/* Panneau droit — formulaire */}
      <Box
        component="main"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 3, sm: 6 },
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 420 }}>
          {/* Logo compact en mobile */}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ display: { xs: 'flex', md: 'none' }, mb: 4 }}
          >
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: 2,
                display: 'grid',
                placeItems: 'center',
                background: 'linear-gradient(135deg, #2bd5dc 0%, #2fe79e 100%)',
                color: 'common.white',
              }}
            >
              <BoltIcon />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                Premium Energy
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Support interne
              </Typography>
            </Box>
          </Stack>

          <Paper
            sx={{
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              boxShadow: '0 20px 60px -20px rgba(15, 23, 42, 0.15)',
            }}
          >
            <Typography component="h1" variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
              {requiresTotp ? 'Vérification en deux étapes' : 'Bienvenue'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {requiresTotp
                ? 'Confirmez votre identité pour continuer.'
                : 'Connectez-vous pour accéder à vos tickets.'}
            </Typography>

            {errorMessage && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                {errorMessage}
              </Alert>
            )}

            {ssoError && (
              <Alert severity="error" role="alert" sx={{ mb: 2 }}>
                La connexion via Microsoft a échoué. Réessayez ou utilisez votre mot de passe.
              </Alert>
            )}

            {!requiresTotp ? (
              <form onSubmit={(e) => void onSubmit(e)} noValidate>
                <Stack spacing={2}>
                  <TextField
                    {...register('email')}
                    id="email"
                    label="Adresse e-mail"
                    type="email"
                    autoComplete="email"
                    required
                    size="medium"
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                    inputProps={{ 'aria-required': true }}
                  />
                  <TextField
                    {...register('password')}
                    id="password"
                    label="Mot de passe"
                    type="password"
                    autoComplete="current-password"
                    required
                    size="medium"
                    error={Boolean(errors.password)}
                    helperText={errors.password?.message}
                    inputProps={{ 'aria-required': true }}
                  />
                  <Stack direction="row" justifyContent="flex-end">
                    <Link
                      component={RouterLink}
                      to="/forgot-password"
                      underline="hover"
                      sx={{ fontSize: 14 }}
                    >
                      Mot de passe oublié ?
                    </Link>
                  </Stack>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={login.isPending}
                    sx={{ py: 1.25, mt: 1 }}
                  >
                    {login.isPending ? 'Connexion…' : 'Se connecter'}
                  </Button>
                  {ssoProviders?.azure && (
                    <>
                      <Divider sx={{ my: 0.5 }}>
                        <Typography variant="caption" color="text.secondary">
                          ou
                        </Typography>
                      </Divider>
                      <Button
                        component="a"
                        href="/api/v1/auth/azure"
                        variant="outlined"
                        size="large"
                        startIcon={<MicrosoftIcon />}
                        sx={{ py: 1.25 }}
                      >
                        Se connecter avec Microsoft
                      </Button>
                    </>
                  )}
                </Stack>
              </form>
            ) : (
              <form onSubmit={onSubmitTotp}>
                <Stack spacing={2}>
                  <Typography variant="body2" color="text.secondary">
                    Entrez le code à 6 chiffres généré par votre application
                    d&apos;authentification, ou un code de secours au format XXXX-XXXX.
                  </Typography>
                  <TextField
                    label="Code"
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value)}
                    autoComplete="one-time-code"
                    required
                    size="medium"
                    inputProps={{
                      inputMode: 'numeric',
                      'aria-label': 'Code de vérification',
                      style: { letterSpacing: '0.4em', textAlign: 'center', fontSize: 18 },
                    }}
                  />
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={login.isPending || totpCode.length < 6}
                    sx={{ py: 1.25 }}
                  >
                    {login.isPending ? 'Vérification…' : 'Valider'}
                  </Button>
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    onClick={() => {
                      setRequiresTotp(null);
                      setTotpCode('');
                    }}
                    sx={{ alignSelf: 'flex-start' }}
                  >
                    Retour
                  </Link>
                </Stack>
              </form>
            )}
          </Paper>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 3 }}
          >
            © {new Date().getFullYear()} Premium Energy — Outil interne
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
